'use client';

import Link from 'next/link';
import { Bookmark, BookmarkCheck, Link2, ArrowUp, ArrowDown } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { cn } from '@/lib/utils';
import { SOURCE_ICONS, SOURCE_LABELS } from '@/lib/constants';
import { ScoreBadge } from './score-badge';
import { Sparkline } from './sparkline';
import { useBookmarkIds, useToggleBookmark } from '@/hooks/use-bookmarks';
import type { ScoredTrend } from '@/hooks/use-trends';

interface TrendCardProps {
  trend: ScoredTrend;
}

function SourceIcon({ source }: { source: string }) {
  const iconName = SOURCE_ICONS[source] as keyof typeof LucideIcons;
  const Icon = (LucideIcons[iconName] as LucideIcons.LucideIcon) || LucideIcons.Globe;
  const label = SOURCE_LABELS[source] || source;

  return (
    <span title={label} className="text-muted-foreground">
      <Icon className="h-4 w-4" />
    </span>
  );
}

export function TrendCard({ trend }: TrendCardProps) {
  const { data: bookmarkIds } = useBookmarkIds();
  const toggleBookmark = useToggleBookmark();
  const isBookmarked = bookmarkIds?.has(trend.id) ?? false;

  const isAccelerating = trend.score?.is_accelerating ?? false;
  const overallScore = trend.score?.overall_score ?? 0;
  const velocityIndicator = trend.score?.velocity_indicator ?? '';

  const isGrowing = velocityIndicator === 'growing' || velocityIndicator === 'accelerating';
  const isDecaying = velocityIndicator === 'decaying' || velocityIndicator === 'declining';

  const growthPercent = trend.normalized_volume > 0
    ? Math.round(trend.normalized_volume)
    : null;

  return (
    <Link
      href={`/trends/${trend.id}`}
      className={cn(
        'group relative block cursor-pointer overflow-hidden rounded-lg p-4 transition-all hover:brightness-110',
        'bg-card',
        isAccelerating
          ? 'border border-indigo-500/50 shadow-[0_0_15px_rgba(99,102,241,0.4)]'
          : 'border border-border'
      )}
    >
      <Sparkline data={trend.recent_volumes} />

      <div className="relative z-10 flex flex-col gap-2">
        {/* Top row: source, language, score */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SourceIcon source={trend.source} />
            <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground uppercase">
              {trend.language}
            </span>
          </div>
          {overallScore > 0 && <ScoreBadge score={overallScore} />}
        </div>

        {/* Title */}
        <h3 className="line-clamp-2 font-semibold text-card-foreground leading-tight">
          {trend.title}
        </h3>

        {/* Growth metric */}
        {growthPercent !== null && (
          <div className="flex items-center gap-1 text-sm">
            {isGrowing && <ArrowUp className="h-3.5 w-3.5 text-green-400" />}
            {isDecaying && <ArrowDown className="h-3.5 w-3.5 text-red-400" />}
            <span
              className={cn(
                'font-medium',
                isGrowing && 'text-green-400',
                isDecaying && 'text-red-400',
                !isGrowing && !isDecaying && 'text-muted-foreground'
              )}
            >
              {growthPercent}%
            </span>
          </div>
        )}

        {/* Monetization preview */}
        {trend.monetization_preview && (
          <p className="line-clamp-1 text-sm text-muted-foreground">
            {trend.monetization_preview}
          </p>
        )}

        {/* Bottom row: correlations + bookmark */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Link2 className="h-3.5 w-3.5" />
            <span>{trend.correlation_count}</span>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleBookmark.mutate({ trendId: trend.id, isBookmarked });
            }}
            className="text-muted-foreground transition-colors hover:text-primary"
          >
            {isBookmarked ? (
              <BookmarkCheck className="h-4 w-4 fill-primary text-primary" />
            ) : (
              <Bookmark className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </Link>
  );
}
